import PropTypes from 'prop-types';
import styles from './card-resource.module.scss';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import Icon, { variants as iconVariants } from '../../../atoms/icon';
import Button from 'react-bootstrap/Button';

const className = augmentClassName(styles);

/**
 * Resource card
 */
const CardResource = ({
  className: classNameProp,
  renderAside,
  renderBody,
  renderTitle,
  renderBottomLink,
  ...props
}) => {
  const moduleClassName = className.string(
    'root',
    'h-100',
    'd-flex',
    'flex-row',
    'justify-content-end',
    'shadow',
    'bg-white'
  );

  const classes = classNameProp ? `${classNameProp} ${moduleClassName}` : moduleClassName;

  return (
    <div className={classes} {...props}>
      {(renderTitle || renderBody) && (
        <div {...className('main', 'flex-grow-1')}>
          {renderTitle && (
            <h3 {...className('title', 'text-uppercase', 'text-bold', 'text-primary')}>
              {renderTitle()}
            </h3>
          )}
          {renderBody && <div {...className('body', 'font-family-sans-serif')}>{renderBody()}</div>}
          {renderBottomLink && <div {...className('bottom-link')}>{renderBottomLink()}</div>}
        </div>
      )}
      {renderAside && <aside {...className('aside', 'text-center', 'ml-3')}>{renderAside()}</aside>}
    </div>
  );
};

CardResource.propTypes = {
  /**
   * HTML classes applied to root
   */
  className: PropTypes.string,
  /**
   * Render prop for aside content
   */
  renderAside: PropTypes.func,
  /**
   * Render prop for body content
   */
  renderBody: PropTypes.func,
  /**
   * Render prop for title content
   */
  renderTitle: PropTypes.func,
  /**
   * Render prop for bottom link content
   */
  renderBottomLink: PropTypes.func
};

const renderAside = iconVariant => (
  <Icon
    variant={iconVariant}
    {...className('text-primary')}
    style={{ fontSize: '28px' }}
    role="img"
    aria-label={iconVariant}
  />
);

const SimpleLink = ({ href, bodyText, linkText, ...props }) => {
  return <MultiLink links={[{ href, text: bodyText }]} bottomLink={linkText} {...props} />;
};

SimpleLink.propTypes = {
  bodyText: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  linkText: PropTypes.string
};

const MultiLink = ({ iconVariant, links, titleText, bottomLink, ...props }) => {
  const renderBody = () => {
    if (!bottomLink) {
      return links.filter(Boolean).map(({ href, text }, index) => (
        <a
          data-key={`${text || ''}-${index}`}
          href={href}
          key={`${text || ''}-${index}`}
          rel="noopener noreferrer"
          target="_blank"
          title="opens in new window"
          {...className('text-black', 'd-block', 'link')}
        >
          {text} <span {...className('sr-only')}>(opens in new window)</span>
        </a>
      ));
    }
    return links.filter(Boolean).map(({ text }, index) => (
      <p
        data-key={`${text || ''}-${index}`}
        key={`${text || ''}-${index}`}
        {...className('text-black', 'd-block', 'break-line')}
      >
        {text}
      </p>
    ));
  };

  const renderBottomLink = () =>
    links.filter(Boolean).map(({ href, text }, index) => (
      <a
        data-key={`${text || ''}-${index}`}
        href={href}
        key={`${text || ''}-${index}`}
        rel="noopener noreferrer"
        target="_blank"
        title="opens in new window"
        {...className('text-black', 'link')}
      >
        {bottomLink} <span {...className('sr-only')}>(opens in new window)</span>
      </a>
    ));

  const renderTitle = () => titleText;
  return (
    <CardResource
      renderAside={() => renderAside(iconVariant)}
      renderBody={renderBody}
      renderTitle={renderTitle}
      renderBottomLink={renderBottomLink}
      {...props}
    />
  );
};
MultiLink.defaultProps = {
  links: []
};
MultiLink.propTypes = {
  iconVariant: PropTypes.oneOf(Object.keys(iconVariants)),
  links: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      href: PropTypes.string
    })
  ),
  titleText: PropTypes.string.isRequired,
  bottomLink: PropTypes.string
};

const ModalControl = ({ iconVariant, onClick, titleText, buttonText, bottomLink, ...props }) => {
  const renderBody = () => {
    if (!bottomLink) {
      return (
        <Button
          variant="link"
          {...className('text-black', 'd-block', 'text-left', 'p-0')}
          onClick={onClick}
        >
          {buttonText}
        </Button>
      );
    }
    return <p {...className('text-black', 'd-block', 'break-line')}>{buttonText}</p>;
  };
  const renderTitle = () => titleText;

  const renderBottomLink = () => (
    <Button
      variant="link"
      {...className('text-black', 'd-block', 'text-left', 'p-0')}
      onClick={onClick}
    >
      {bottomLink}
    </Button>
  );

  return (
    <CardResource
      renderAside={() => renderAside(iconVariant)}
      renderBody={renderBody}
      renderTitle={renderTitle}
      renderBottomLink={bottomLink && renderBottomLink}
      {...props}
    />
  );
};

ModalControl.propTypes = {
  iconVariant: PropTypes.oneOf(Object.keys(iconVariants)),
  onClick: PropTypes.func,
  titleText: PropTypes.string,
  buttonText: PropTypes.string,
  bottomLink: PropTypes.string
};

CardResource.SimpleLink = SimpleLink;
CardResource.MultiLink = MultiLink;
CardResource.ModalControl = ModalControl;

export default CardResource;
